import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Box,
  SimpleGrid,
  useToast,
  Flex,
  Button,
  HStack,
  Select,
  Input,
  Text,
} from '@chakra-ui/react';
import DynamicTable from '../../../components/table/DynamicTable.js';
import { columnsDataStudies } from './variables/columnsDataStudies.js';
import { getData } from '../../../utils/httpClient.js';
import { useSearch } from '../../../contexts/SearchContext.js';
import CreateModal from '../../../components/examInscription/CreateModal.js';
import ConfirmModal from '../../../components/modal/ConfirmModal.js';

// Filtros soportados por GET /exam/inscriptions
// (examScheduleId, studentId, examPeriodId, status, startDate, endDate).
const initialFilters = {
  examPeriodId: '',
  status: '',
  studentId: '',
};

export default function Settings() {
  const { searchQuery } = useSearch();
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [modalState, setModalState] = useState({
    isOpen: false,
    confirmModalOpen: false,
  });

  const [periods, setPeriods] = useState([]);
  const [students, setStudents] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [lastInscription, setLastInscription] = useState(null);

  const rowsPerPage = 10;
  const toast = useToast();

  const filterQuery = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page + 1));
    params.set('limit', String(rowsPerPage));
    if (searchQuery) params.set('search', searchQuery);
    if (filters.examPeriodId) params.set('examPeriodId', filters.examPeriodId);
    if (filters.status) params.set('status', filters.status);
    if (filters.studentId) params.set('studentId', filters.studentId);
    return params.toString();
  }, [page, searchQuery, filters]);

  const latestRequestRef = useRef(0);

  const getStudies = async () => {
    const requestId = ++latestRequestRef.current;
    try {
      const response = await getData(`/exam/inscriptions?${filterQuery}`);
      if (requestId !== latestRequestRef.current) return;
      const rows = response?.payload?.inscriptions || [];

      const mapped = rows.map((r) => {
        const fullName = [r.student_name, r.student_last_name]
          .filter(Boolean)
          .join(' ');
        const examDateTime =
          r.exam_date && r.exam_time
            ? `${r.exam_date}T${r.exam_time}`
            : r.exam_date || null;
        return {
          id: r.id,
          alumno: fullName || '-',
          documento: r.student_document || '-',
          estudio: r.study_name || r.subject_name || '-',
          plan: r.period_name || '-',
          ciclo: r.subject_cycle || '-',
          examenDate: examDateTime,
          inscriptionDate: r.inscription_date || r.created_date || null,
        };
      });

      setData(mapped);
      setTotalPages(
        Math.max(1, Math.ceil((response?.payload?.total || mapped.length) / rowsPerPage)),
      );
      // La fila más reciente del backend ya viene ordenada DESC.
      if (rows[0]) setLastInscription(rows[0]);
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      setData([]);
      setTotalPages(0);
      toast({
        title: 'Error al obtener inscripciones a exámenes',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const loadFilterCatalogs = async () => {
    try {
      const [pRes, sRes] = await Promise.all([
        getData('/exam/periods?page=1&limit=200'),
        getData('/person/student?page=1&limit=500'),
      ]);
      setPeriods(pRes?.payload?.periods || pRes?.payload || []);
      setStudents(sRes?.payload?.people || []);
    } catch (e) {
      // Silencioso: sólo UX.
    }
  };

  useEffect(() => {
    loadFilterCatalogs();
  }, []);

  useEffect(() => {
    getStudies();
  }, [filterQuery]);

  const openModal = () => setModalState({ isOpen: true, confirmModalOpen: false });
  const closeModal = () => setModalState({ isOpen: false, confirmModalOpen: false });
  const showConfirmModal = () =>
    setModalState({ isOpen: true, confirmModalOpen: true });
  const hiddeConfirmModal = () =>
    setModalState({ isOpen: true, confirmModalOpen: false });

  const updateFilter = (key, value) => {
    setPage(0);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setPage(0);
    setFilters(initialFilters);
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {lastInscription && (
        <Box bg="white" p={4} borderRadius="md" boxShadow="sm" mb={4}>
          <Text fontSize="sm" color="gray.500">
            Última inscripción registrada
          </Text>
          <Text fontWeight="bold">
            {[lastInscription.student_name, lastInscription.student_last_name]
              .filter(Boolean)
              .join(' ') || 'Alumno'}{' '}
            → {lastInscription.subject_name || 'Examen'}{' '}
            {lastInscription.exam_date
              ? `(${new Date(lastInscription.exam_date).toLocaleDateString('es-ES')})`
              : ''}
          </Text>
          <Text fontSize="xs" color="gray.500">
            Inscripción:{' '}
            {lastInscription.inscription_date
              ? new Date(lastInscription.inscription_date).toLocaleString('es-ES')
              : '-'}
          </Text>
        </Box>
      )}

      <Box bg="white" p={4} borderRadius="md" boxShadow="sm" mb={4}>
        <HStack spacing={3} flexWrap="wrap">
          <Select
            placeholder="Período"
            size="sm"
            maxW="200px"
            value={filters.examPeriodId}
            onChange={(e) => updateFilter('examPeriodId', e.target.value)}
          >
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || `${p.year}-${p.period_number ?? p.periodNumber}`}
              </option>
            ))}
          </Select>
          <Select
            placeholder="Alumno"
            size="sm"
            maxW="260px"
            value={filters.studentId}
            onChange={(e) => updateFilter('studentId', e.target.value)}
          >
            {students.map((p) => (
              <option key={p.id} value={p.studentId || p.id}>
                {[p.name, p.lastName].filter(Boolean).join(' ')}
                {p.document ? ` (${p.document})` : ''}
              </option>
            ))}
          </Select>
          <Select
            placeholder="Estado"
            size="sm"
            maxW="180px"
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
          >
            <option value="INSCRIBED">INSCRIBED</option>
            <option value="CONFIRMED">CONFIRMED</option>
            <option value="PRESENT">PRESENT</option>
            <option value="ABSENT">ABSENT</option>
            <option value="CANCELLED">CANCELLED</option>
          </Select>
          <Button size="sm" variant="outline" onClick={clearFilters}>
            Limpiar
          </Button>
        </HStack>
      </Box>

      <Flex justify="flex-end" mb={4}>
        <Button colorScheme="blue" onClick={() => openModal()}>
          Nueva inscripción
        </Button>
      </Flex>
      <SimpleGrid
        mb="20px"
        columns={{ sm: 1 }}
        spacing={{ base: '20px', xl: '20px' }}
      >
        <DynamicTable
          columnsData={columnsDataStudies}
          data={data}
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          openModal={openModal}
        />
      </SimpleGrid>

      <CreateModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        onSuccess={() => getStudies()}
      />

      <ConfirmModal
        isOpen={modalState.confirmModalOpen}
        onClose={hiddeConfirmModal}
        onConfirm={closeModal}
        header="Confirmación de Cancelación"
        message="¿Estás seguro de que deseas cancelar? Todos los datos no guardados se perderán."
      />
    </Box>
  );
}
